import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { apiFetch } from "../../api";
import Stroskovnik from "../Stroskovnik/Stroskovnik";
import "../PodatkiDolznika/PodatkiDolznika.css";
import "./NovPrimer.css";

const prikazi = (value) => {
    const v = String(value || "").trim();
    return v || "—";
};

const normalizirajPrijavljenegaUporabnika = (data) => {
    const polnoIme =
        (data?.fullName && data.fullName.trim()) ||
        [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim() ||
        data?.email ||
        "";

    return {
        polnoIme,
        email: data?.email || "",
        naslov: data?.address || "",
        gsm: data?.gsm || "",
        idZaDdv: data?.idZaDdv || "",
        maticnaStevilka: data?.maticnaStevilka || "",
        transakcijskiRacunStevilka: data?.transakcijskiRacunStevilka || "",
        nazivBanke: data?.nazivBanke || data?.bankName || data?.banka || "",
    };
};

const NOV_PRIMER_EXPORT_WIDTH_PX = 794;
const NOV_PRIMER_LOGO_SRC = "/grb.png";

const OSNOVNE_BANKE = [
    "NLB d.d.",
    "OTP banka d.d.",
    "Banka Intesa Sanpaolo d.d.",
    "UniCredit Banka Slovenija d.d.",
    "Gorenjska banka d.d.",
    "Banka Sparkasse d.d.",
    "Delavska hranilnica d.d.",
    "Addiko Bank d.d.",
    "Deželna banka Slovenije d.d.",
    "Hranilnica LON d.d.",
    "Primorska hranilnica Vipava d.d.",
];

const MOZNOSTI_BANK = [...OSNOVNE_BANKE, "Druga banka"];

const MOZNOSTI_PREDMETA = ["Premičnine", "Plača", "TRR", "Vozilo", "Drugo"];
const MOZNOSTI_SREDSTVA = ["Rubež premičnin", "Rubež plače", "Rubež TRR", "Drugo"];
const VRSTE_IZVRSILNEGA_DEJANJA = ["Rubež", "Cenitev", "Odvoz", "Prodaja", "Izpraznitev", "Drugo"];
const SREDSTVA_IN_PREDMETI_IZVRSBE = ["Premičnine", "Plača", "TRR", "Vozilo", "Nepremičnina", "Drugo"];
const VARSCINA_STATUSI = ["Plačano", "Neporavnano"];
const NACINI_IZTERJAVE = ["Gotovina", "TRR"];

const ustvariPrazniPrejsnjiRubez = () => ({
    stevilkaRubeza: "",
    izvrsitelj: "",
    glavnicaZarubljena: "",
    predmetRubeza: "",
    sredstvoRubeza: "",
    stevilkaZadeve: "",
});

const onlyDigits = (v, max = 10) =>
    String(v || "").replace(/\D/g, "").slice(0, max);

const formatirajTrrVnos = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 17);
    return digits.replace(/(.{4})/g, "$1 ").trim();
};

const formatirajTelefonVnos = (value) => {
    let v = String(value || "");
    const imelZakljucekSPresledkom = /\s$/.test(v);

    v = v.replace(/[^\d+ ]/g, "");
    const imaZacetniPlus = v.trimStart().startsWith("+");
    v = v.replace(/\+/g, "");
    v = v.replace(/\s+/g, " ");
    v = v.replace(/^\s+/, "");

    if (imaZacetniPlus) v = "+" + v;
    if (v.length > 20) v = v.slice(0, 20);
    if (imelZakljucekSPresledkom && !v.endsWith(" ")) v += " ";

    return v;
};

const parseMoney = (v) => {
    if (v == null) return null;

    let s = String(v).trim();
    if (!s) return null;

    s = s.replace(/[^\d.,-]/g, "");

    const jeNegativno = s.startsWith("-");
    s = s.replace(/-/g, "");

    const imaVejico = s.includes(",");
    const imaPiko = s.includes(".");
    const steviloPik = (s.match(/\./g) || []).length;

    if (imaVejico && imaPiko) {
        s = s.replace(/\./g, "").replace(",", ".");
    } else if (imaVejico) {
        s = s.replace(",", ".");
    } else if (imaPiko) {
        const zadnjaPika = s.lastIndexOf(".");
        const decimalk = s.length - zadnjaPika - 1;

        if (steviloPik > 1 || decimalk === 3) {
            s = s.replace(/\./g, "");
        }
    }

    if (jeNegativno) s = "-" + s;

    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const formatMoneySl = (v) => {
    const n = typeof v === "number" ? v : parseMoney(v);

    if (n == null) return "";

    return n.toLocaleString("sl-SI", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const cleanMoneyInput = (v) =>
    String(v || "").replace(/[^\d.,]/g, "").slice(0, 20);

const jeVeljavenEmail = (v) => {
    const s = (v || "").trim();
    if (!s) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const filenameSafe = (v) =>
    String(v || "nov-primer")
        .trim()
        .replace(/[^\p{L}\p{N}_-]+/gu, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80) || "nov-primer";

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const exportValue = (value) => {
    const s = String(value ?? "").trim();
    return s ? escapeHtml(s) : "—";
};

const exportMoney = (value) => {
    const n = typeof value === "number" ? value : parseMoney(value);

    if (n == null || !Number.isFinite(n)) return "—";

    return `${n.toLocaleString("sl-SI", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} €`;
};

const exportRowsHtml = (rows) =>
    rows
        .map(
            ([label, value]) => `
                <div class="np-export-row">
                    <div class="np-export-label">${escapeHtml(label)}</div>
                    <div class="np-export-value">${value}</div>
                </div>
            `
        )
        .join("");

const exportSectionHtml = (title, rows, extraHtml = "") => `
    <section class="np-export-card np-export-block">
        <h2>${escapeHtml(title)}</h2>
        <div class="np-export-rows">
            ${exportRowsHtml(rows)}
        </div>
        ${extraHtml}
    </section>
`;

const getNovPrimerExportCss = () => `
    @page {
        size: A4 portrait;
        margin: 10mm;
    }

    html,
    body {
        margin: 0;
        padding: 0;
        background: #ffffff !important;
        color: #111827 !important;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 12px;
        line-height: 1.35;
    }

    * {
        box-sizing: border-box;
    }

    .np-export-doc {
        width: ${NOV_PRIMER_EXPORT_WIDTH_PX}px;
        max-width: ${NOV_PRIMER_EXPORT_WIDTH_PX}px;
        background: #ffffff !important;
        color: #111827 !important;
        padding: 0;
        margin: 0;
    }

    .np-export-block {
        break-inside: avoid;
        page-break-inside: avoid;
    }

    .np-export-header {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding-bottom: 16px;
        margin-bottom: 18px;
        border-bottom: 1px solid rgba(148, 163, 184, 0.35);
    }

    .np-export-currentUser {
        width: 100%;
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 14px;
        align-items: stretch;
    }

    .np-export-userTower {
        min-height: 146px;
        padding: 12px;
        box-sizing: border-box;
        background: #ffffff;
        color: #111827;
    }

    .np-export-userTower--left {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        text-align: center;
    }

    .np-export-userLogoBox {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        text-align: center;
    }

.np-export-userLogoBox img {
    display: block;
    width: auto !important;
    height: auto !important;
    max-width: 62px;
    max-height: 62px;
    object-fit: contain;
}

    .np-export-userLogoText {
        color: #64748b;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.15;
        letter-spacing: 0.06em;
        text-transform: uppercase;
    }

    .np-export-userMainData {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: center;
        text-align: center;
    }

    .np-export-userMainData strong {
        color: #111827;
        font-size: 15px;
        font-weight: 600;
        line-height: 1.2;
        overflow-wrap: anywhere;
    }

    .np-export-userMainData span {
        color: #64748b;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.25;
        overflow-wrap: anywhere;
    }

    .np-export-userTower--right {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        gap: 7px;
        padding-top: 28px;
    }

    .np-export-userRow {
        min-width: 280px;
        display: grid;
        grid-template-columns: max-content max-content;
        column-gap: 18px;
        align-items: center;
        min-height: 28px;
        padding: 6px 9px;
        box-sizing: border-box;
    }

    .np-export-userRow span {
        color: #64748b;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        line-height: 1.2;
    }

    .np-export-userRow strong {
        color: #111827;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.25;
        text-align: left;
        overflow-wrap: anywhere;
    }

    .np-export-caseMeta {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        padding-top: 14px;
        border-top: 1px solid rgba(148, 163, 184, 0.35);
    }

    .np-export-metaItem {
        border: 1px solid #d1d5db;
        border-radius: 8px;
        padding: 8px;
        background: #f9fafb;
        min-width: 0;
    }

    .np-export-metaItem strong {
        display: block;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #6b7280;
        margin-bottom: 3px;
    }

    .np-export-metaItem span {
        display: block;
        color: #111827;
        overflow-wrap: anywhere;
    }

    .np-export-card {
        margin: 0 0 12px;
        padding: 12px;
        border: 1px solid #d1d5db;
        border-radius: 10px;
        background: #ffffff;
    }

    .np-export-card h2 {
        margin: 0 0 9px;
        padding-bottom: 5px;
        border-bottom: 1px solid #e5e7eb;
        color: #111827;
        font-size: 16px;
        line-height: 1.2;
    }

    .np-export-rows {
        display: grid;
        gap: 0;
    }

    .np-export-row {
        display: grid;
        grid-template-columns: 185px minmax(0, 1fr);
        gap: 10px;
        padding: 6px 0;
        border-bottom: 1px solid #edf2f7;
        break-inside: avoid;
        page-break-inside: avoid;
    }

    .np-export-row:last-child {
        border-bottom: none;
    }

    .np-export-label {
        color: #4b5563;
        font-weight: 700;
        overflow-wrap: anywhere;
    }

    .np-export-value {
        color: #111827;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
    }

    .np-export-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        margin-top: 8px;
        font-size: 11.5px;
    }

    .np-export-table tr {
        break-inside: avoid;
        page-break-inside: avoid;
    }

    .np-export-table th,
    .np-export-table td {
        border: 1px solid #d1d5db;
        padding: 6px;
        vertical-align: top;
        color: #111827;
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .np-export-table th {
        background: #f3f4f6;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 10px;
        color: #374151;
    }

    .np-export-table td:last-child,
    .np-export-table th:last-child {
        text-align: right;
    }

    .np-export-muted {
        color: #6b7280;
        margin: 6px 0 0;
        font-size: 12px;
    }

    .np-export-summary {
        margin-top: 9px;
        display: grid;
        justify-content: end;
        gap: 4px;
        text-align: right;
        font-size: 13px;
    }

    .np-export-summary strong {
        color: #111827;
    }

    @media print {
        html,
        body {
            background: #ffffff !important;
        }

        .np-export-doc {
            width: 100% !important;
            max-width: none !important;
        }
    }
`;

const waitForImagesIn = async (root) => {
    const images = Array.from(root.querySelectorAll("img"));

    await Promise.all(
        images.map((img) => {
            if (img.complete) return Promise.resolve();

            return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
            });
        })
    );
};

export default function NovPrimer() {
    const navigate = useNavigate();
    const printRef = useRef(null);

    const [primerId, setPrimerId] = useState(null);

    const [stevilkaSpisaIzvrsitelja, setStevilkaSpisaIzvrsitelja] = useState("");
    const [opravilnaStevilkaSodisca, setOpravilnaStevilkaSodisca] = useState("");
    const [imeIzvrsitelja, setImeIzvrsitelja] = useState("");
    const [prijavljenUporabnik, setPrijavljenUporabnik] = useState(null);

    const [sporocilo, setSporocilo] = useState("");
    const [napaka, setNapaka] = useState("");
    const [nalaganje, setNalaganje] = useState(false);
    const [shranjevanje, setShranjevanje] = useState(false);
    const [izvoz, setIzvoz] = useState(false);

    const [stroskovnik, setStroskovnik] = useState(null);
    const [stroskovnikValueKey, setStroskovnikValueKey] = useState("nov-primer");

    const [upnikDavcna, setUpnikDavcna] = useState("");
    const [ajpesUpnik, setAjpesUpnik] = useState(null);
    const [zastopanPo, setZastopanPo] = useState("");
    const [pooblascenec, setPooblascenec] = useState("");
    const [glavnicaTerjatve, setGlavnicaTerjatve] = useState("");
    const [nazivBanke, setNazivBanke] = useState("");
    const [poljubnaBanka, setPoljubnaBanka] = useState("");
    const [trrUpnika, setTrrUpnika] = useState("");
    const [telefonUpnika, setTelefonUpnika] = useState("");
    const [kontaktnaOseba, setKontaktnaOseba] = useState("");
    const [gsmUpnika, setGsmUpnika] = useState("");
    const [ePostaUpnika, setEPostaUpnika] = useState("");
    const [statusUpnika, setStatusUpnika] = useState("koncni");
    const [davcniZavezanec, setDavcniZavezanec] = useState(false);
    const [opombeUpnika, setOpombeUpnika] = useState("");
    const [nazivNaRacun, setNazivNaRacun] = useState("");

    const [dolznikDavcna, setDolznikDavcna] = useState("");
    const [ajpesDolznik, setAjpesDolznik] = useState(null);
    const [fizicnaOseba, setFizicnaOseba] = useState("");
    const [telefonDolznika, setTelefonDolznika] = useState("");
    const [gsmDolznika, setGsmDolznika] = useState("");
    const [ePostaDolznika, setEPostaDolznika] = useState("");
    const [dolznikoviTrrji, setDolznikoviTrrji] = useState([{ trr: "" }]);
    const [dolznikoveBanke, setDolznikoveBanke] = useState([{ banka: "", poljubnaBanka: "" }]);
    const [znesekDolga, setZnesekDolga] = useState("");
    const [glavnica, setGlavnica] = useState("");
    const [obrestiOd, setObrestiOd] = useState("");
    const [obrestiDo, setObrestiDo] = useState("");
    const [stroski, setStroski] = useState("");
    const [skupajDolgNaDan, setSkupajDolgNaDan] = useState("");
    const [prejsnjiRubezi, setPrejsnjiRubezi] = useState([ustvariPrazniPrejsnjiRubez()]);

    const [porociloSodisce, setPorociloSodisce] = useState("");
    const [porociloZapSt, setPorociloZapSt] = useState("");
    const [porociloStAkta, setPorociloStAkta] = useState("");
    const [porociloDatumAkta, setPorociloDatumAkta] = useState("");
    const [porociloVrstaIzvrsilnegaDejanja, setPorociloVrstaIzvrsilnegaDejanja] = useState("");
    const [porociloSredstvoInPredmetIzvrsbe, setPorociloSredstvoInPredmetIzvrsbe] = useState("");
    const [porociloOpravljenaDejanja, setPorociloOpravljenaDejanja] = useState("");
    const [porociloDatumPravnomocnosti, setPorociloDatumPravnomocnosti] = useState("");
    const [porociloVarscinaStatus, setPorociloVarscinaStatus] = useState("");
    const [porociloVarscinaIzvrsitelj, setPorociloVarscinaIzvrsitelj] = useState("");
    const [porociloVarscinaSodisce, setPorociloVarscinaSodisce] = useState("");
    const [porociloDatumSklepa, setPorociloDatumSklepa] = useState("");
    const [porociloVisinaIzterjanega, setPorociloVisinaIzterjanega] = useState("");
    const [porociloNacinIzterjave, setPorociloNacinIzterjave] = useState("");
    const [porociloDatumOddajePorocila, setPorociloDatumOddajePorocila] = useState("");
    const [porociloVisinaPlacila, setPorociloVisinaPlacila] = useState("");
    const [porociloOpombePorocila, setPorociloOpombePorocila] = useState("");

    useEffect(() => {
        let active = true;

        (async () => {
            try {
                const res = await apiFetch("/api/auth/me");
                if (!res.ok) return;

                const data = await res.json();
                if (!active) return;

                const uporabnik = normalizirajPrijavljenegaUporabnika(data);

                setPrijavljenUporabnik(uporabnik);
                setImeIzvrsitelja(uporabnik.polnoIme);

                setPrejsnjiRubezi((old) =>
                    old.map((r) => ({
                        ...r,
                        izvrsitelj: r.izvrsitelj || uporabnik.polnoIme,
                    }))
                );
            } catch {
                // ignore
            }
        })();

        return () => {
            active = false;
        };
    }, []);

    const imeUpnika = ajpesUpnik?.naziv || "";
    const naslovUpnika = ajpesUpnik?.naslov || "";
    const postaUpnika = ajpesUpnik?.posta || "";
    const drzavaUpnika = ajpesUpnik?.drzava || "";
    const davcnaStevilkaUpnika = ajpesUpnik?.davcnaStevilka || "";

    const imeDolznika = ajpesDolznik?.naziv || "";
    const naslovDolznika = ajpesDolznik?.naslov || "";
    const postaDolznika = ajpesDolznik?.posta || "";
    const drzavaDolznika = ajpesDolznik?.drzava || "";
    const davcnaStevilkaDolznika = ajpesDolznik?.davcnaStevilka || "";

    const osnovnaGlavnicaZaObresti = useMemo(() => {
        return (
            parseMoney(glavnica) ??
            parseMoney(glavnicaTerjatve) ??
            parseMoney(znesekDolga) ??
            ""
        );
    }, [glavnica, glavnicaTerjatve, znesekDolga]);

    const stroskovnikInitialCase = useMemo(() => {
        return {
            executorName: imeIzvrsitelja,
            stevilkaSpisaIzvrsitelja,
            opravilnaStevilkaSodisca,
            upnik: {
                davcnaAliMaticna: upnikDavcna,
                ajpesUpnik,
            },
            dolznik: {
                davcnaAliMaticna: dolznikDavcna,
                ajpesDolznik,
            },
        };
    }, [
        imeIzvrsitelja,
        stevilkaSpisaIzvrsitelja,
        opravilnaStevilkaSodisca,
        upnikDavcna,
        ajpesUpnik,
        dolznikDavcna,
        ajpesDolznik,
    ]);

    const buildExportUserTowersHtml = () => {
        const u = prijavljenUporabnik || {};

        return `
        <div class="np-export-currentUser">
            <div class="np-export-userTower np-export-userTower--left">
                <div class="np-export-userLogoBox">
                    <img src="${escapeHtml(NOV_PRIMER_LOGO_SRC)}" alt="IZVRŠITELJ" />
                    <div class="np-export-userLogoText">REPUBLIKA SLOVENIJA</div>
                </div>

                <div class="np-export-userMainData">
                    <strong>${exportValue(u.polnoIme || imeIzvrsitelja)}</strong>
                    <span>${exportValue(u.naslov)}</span>
                    <span>${exportValue(u.gsm)}</span>
                    <span>${exportValue(u.email)}</span>
                </div>
            </div>

            <div class="np-export-userTower np-export-userTower--right">
                <div class="np-export-userRow">
                    <span>ID za DDV</span>
                    <strong>${exportValue(u.idZaDdv)}</strong>
                </div>

                <div class="np-export-userRow">
                    <span>Matična številka</span>
                    <strong>${exportValue(u.maticnaStevilka)}</strong>
                </div>

                <div class="np-export-userRow">
                    <span>TRR</span>
                    <strong>${exportValue(u.transakcijskiRacunStevilka)}</strong>
                </div>

                <div class="np-export-userRow">
                    <span>Banka</span>
                    <strong>${exportValue(u.nazivBanke)}</strong>
                </div>
            </div>
        </div>
    `;
    };

    const buildNovPrimerExportBody = () => {
        const stroskovnikItems = Array.isArray(stroskovnik?.items) ? stroskovnik.items : [];
        const pointValue = Number.isFinite(Number(stroskovnik?.pointValue)) ? Number(stroskovnik.pointValue) : 0.48;

        const stroskovnikItemsHtml = stroskovnikItems.length
            ? `
                <table class="np-export-table">
                    <thead>
                        <tr>
                            <th style="width: 46%;">Postavka</th>
                            <th style="width: 14%;">Tarifa</th>
                            <th style="width: 12%;">Točke</th>
                            <th style="width: 10%;">Kol.</th>
                            <th style="width: 18%;">Znesek</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${stroskovnikItems
                .map((item) => {
                    const isKm = Number(item.id) === 10;
                    const amount = isKm
                        ? Number(stroskovnik?.kilometrinaResult?.costEUR)
                        : Number(item.points || 0) * Number(item.qty || 0) * pointValue;

                    return `
                            <tr>
                                <td>${exportValue(item.name)}</td>
                                <td>${exportValue(isKm ? "" : item.tarifa)}</td>
                                <td>${isKm ? "—" : exportValue(item.points)}</td>
                                <td>${isKm ? "—" : exportValue(item.qty)}</td>
                                <td>${exportMoney(Number.isFinite(amount) ? amount : 0)}</td>
                            </tr>
                        `;
                })
                .join("")}
                    </tbody>
                </table>
            `
            : `<p class="np-export-muted">Stroškovnik še nima postavk.</p>`;

        const prejsnjiRubeziHtml = Array.isArray(prejsnjiRubezi) && prejsnjiRubezi.length
            ? `
                <table class="np-export-table">
                    <thead>
                        <tr>
                            <th>Kateri rubež</th>
                            <th>Predmet</th>
                            <th>Sredstvo</th>
                            <th>Glavnica zarubljena</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${prejsnjiRubezi
                .map(
                    (r) => `
                            <tr>
                                <td>${exportValue(r.stevilkaRubeza ? `${r.stevilkaRubeza}. rubež` : "")}</td>
                                <td>${exportValue(r.predmetRubeza)}</td>
                                <td>${exportValue(r.sredstvoRubeza)}</td>
                                <td>${exportMoney(r.glavnicaZarubljena)}</td>
                            </tr>
                        `
                )
                .join("")}
                    </tbody>
                </table>
            `
            : `<p class="np-export-muted">Ni vpisanih predhodnih rubežev.</p>`;

        const kilometrinaRows = stroskovnik?.kilometrinaResult
            ? [
                ["Razdalja", exportValue(`${Number(stroskovnik.kilometrinaResult.distanceKm || 0).toFixed(1)} km`)],
                ["Vrednost/km", exportMoney(stroskovnik.kilometrinaResult.rateEURPerKm)],
                ["Znesek", exportMoney(stroskovnik.kilometrinaResult.costEUR)],
            ]
            : [["Kilometrina", "Ni izračunana"]];

        const obrestiRows = stroskovnik?.obrestiResult
            ? [
                ["Glavnica", exportMoney(stroskovnik.obrestiResult.principalSum)],
                ["Obresti", exportMoney(stroskovnik.obrestiResult.interest)],
                ["Skupaj", exportMoney(stroskovnik.obrestiResult.total)],
                ["Število dni", exportValue(stroskovnik.obrestiResult.days)],
                ["Začetni datum", exportValue(stroskovnik.obrestiResult.startDate)],
                ["Končni datum", exportValue(stroskovnik.obrestiResult.endDate)],
            ]
            : [["Obračun obresti", "Ni izračunan"]];

        return `
    <div class="np-export-doc">
        <header class="np-export-header np-export-block">
            ${buildExportUserTowersHtml()}

            <div class="np-export-caseMeta">
                <div class="np-export-metaItem">
                    <strong>Št. spisa izvršitelja</strong>
                    <span>${exportValue(stevilkaSpisaIzvrsitelja)}</span>
                </div>

                <div class="np-export-metaItem">
                    <strong>Opr. št. sodišča</strong>
                    <span>${exportValue(opravilnaStevilkaSodisca)}</span>
                </div>
            </div>
        </header>

                ${exportSectionHtml("Podatki o upniku", [
            ["Davčna / matična", exportValue(upnikDavcna)],
            ["Naziv", exportValue(imeUpnika)],
            ["Naslov", exportValue(naslovUpnika)],
            ["Pošta", exportValue(postaUpnika)],
            ["Država", exportValue(drzavaUpnika)],
            ["Davčna št.", exportValue(davcnaStevilkaUpnika)],
            ["E-pošta", exportValue(ePostaUpnika)],
            ["Pooblaščenec", exportValue(pooblascenec)],
            ["Zastopan po", exportValue(zastopanPo)],
            ["Glavnica terjatve", exportMoney(glavnicaTerjatve)],
            ["Banka", exportValue(nazivBanke === "Druga banka" ? poljubnaBanka : nazivBanke)],
            ["TRR", exportValue(trrUpnika)],
            ["GSM", exportValue(gsmUpnika)],
            ["Kontakt", exportValue(telefonUpnika)],
            ["Kontaktna oseba", exportValue(kontaktnaOseba)],
            ["Status upnika", exportValue(statusUpnika)],
            ["Davčni zavezanec", davcniZavezanec ? "Da" : "Ne"],
            ["Opombe", exportValue(opombeUpnika)],
            ["Naziv na račun", exportValue(nazivNaRacun)],
        ])}

                ${exportSectionHtml("Podatki o dolžniku", [
            ["Davčna / matična", exportValue(dolznikDavcna)],
            ["Ime / firma", exportValue(imeDolznika)],
            ["Naslov", exportValue(naslovDolznika)],
            ["Pošta", exportValue(postaDolznika)],
            ["Država", exportValue(drzavaDolznika)],
            ["Davčna št.", exportValue(davcnaStevilkaDolznika)],
            ["Fizična oseba", exportValue(fizicnaOseba)],
            ["GSM", exportValue(gsmDolznika)],
            ["Kontakt", exportValue(telefonDolznika)],
            ["E-pošta", exportValue(ePostaDolznika)],
            ["Glavnica", exportMoney(glavnica)],
            ["Stroški", exportMoney(stroski)],
            ["Skupaj dolg na dan", exportMoney(skupajDolgNaDan)],
            ["Znesek dolga", exportMoney(znesekDolga)],
            ["Obresti od", exportValue(obrestiOd)],
            ["Obresti do", exportValue(obrestiDo)],
        ])}

                <section class="np-export-card np-export-block">
                    <h2>Predhodno zarubljene premičnine</h2>
                    ${prejsnjiRubeziHtml}
                </section>

                <section class="np-export-card np-export-block">
                    <h2>Stroškovnik</h2>
                    ${stroskovnikItemsHtml}

                    <div class="np-export-summary">
                        <div>Skupaj brez DDV: <strong>${exportMoney(stroskovnik?.sum)}</strong></div>
                        <div>DDV: <strong>${exportMoney(stroskovnik?.ddv)}</strong></div>
                        <div>Skupaj z DDV: <strong>${exportMoney(stroskovnik?.total)}</strong></div>
                    </div>
                </section>

                ${exportSectionHtml("Kilometrina", kilometrinaRows)}
                ${exportSectionHtml("Obračun obresti", obrestiRows)}

                ${exportSectionHtml("Poročilo / podatki zadeve", [
            ["Sodišče", exportValue(porociloSodisce)],
            ["Zap. št.", exportValue(porociloZapSt)],
            ["Št. akta", exportValue(porociloStAkta)],
            ["Datum akta", exportValue(porociloDatumAkta)],
            ["Vrsta izvršilnega dejanja", exportValue(porociloVrstaIzvrsilnegaDejanja)],
            ["Sredstvo in predmet izvršbe", exportValue(porociloSredstvoInPredmetIzvrsbe)],
            ["Opravljena dejanja", exportValue(porociloOpravljenaDejanja)],
            ["Datum pravnomočnosti", exportValue(porociloDatumPravnomocnosti)],
            ["Varščina – status", exportValue(porociloVarscinaStatus)],
            ["Varščina izvršitelj", exportMoney(porociloVarscinaIzvrsitelj)],
            ["Varščina sodišče", exportMoney(porociloVarscinaSodisce)],
            ["Datum sklepa", exportValue(porociloDatumSklepa)],
            ["Višina izterjanega", exportMoney(porociloVisinaIzterjanega)],
            ["Način izterjave", exportValue(porociloNacinIzterjave)],
            ["Datum oddaje poročila", exportValue(porociloDatumOddajePorocila)],
            ["Višina plačila", exportMoney(porociloVisinaPlacila)],
            ["Opombe poročila", exportValue(porociloOpombePorocila)],
        ])}
            </div>
        `;
    };

    const buildNovPrimerExportHtml = () => `<!doctype html>
<html lang="sl">
<head>
    <meta charset="utf-8" />
    <title>${escapeHtml(filenameSafe(stevilkaSpisaIzvrsitelja || opravilnaStevilkaSodisca || "nov-primer"))}</title>
    <style>${getNovPrimerExportCss()}</style>
</head>
<body>
    ${buildNovPrimerExportBody()}
</body>
</html>`;

    const pridobiAjpes = async (tip) => {
        setNapaka("");
        setSporocilo("");
        setNalaganje(true);

        const stevke = tip === "upnik" ? onlyDigits(upnikDavcna) : onlyDigits(dolznikDavcna);

        if (!(stevke.length === 8 || stevke.length === 10)) {
            setNalaganje(false);
            setNapaka("Vpiši davčno (8) ali matično (10) številk.");
            return;
        }

        try {
            const res = await apiFetch(`/api/ajpes/prs/${stevke}`, { method: "GET" });

            if (!res.ok) {
                let msg = "";

                try {
                    const json = await res.json();
                    msg = json?.message || "";
                } catch {
                    msg = await res.text().catch(() => "");
                }

                throw new Error(msg || "AJPES poizvedba ni uspela.");
            }

            const data = await res.json();

            if (tip === "upnik") {
                setUpnikDavcna(stevke);
                setAjpesUpnik(data);
                setNazivNaRacun((old) => old.trim() ? old : data?.naziv || "");
            } else {
                setDolznikDavcna(stevke);
                setAjpesDolznik(data);
            }
        } catch (e) {
            setNapaka(e?.message || "Napaka pri AJPES poizvedbi.");
        } finally {
            setNalaganje(false);
        }
    };

    const naloziPrimerPoZadevi = async () => {
        setNapaka("");
        setSporocilo("");

        const spis = stevilkaSpisaIzvrsitelja.trim();
        const opravilna = opravilnaStevilkaSodisca.trim();

        if (!spis && !opravilna) {
            setNapaka("Vnesite št. spisa izvršitelja ali opr. št. sodišča.");
            return;
        }

        setNalaganje(true);

        try {
            const params = new URLSearchParams();

            if (spis) params.set("stevilkaSpisaIzvrsitelja", spis);
            if (opravilna) params.set("opravilnaStevilkaSodisca", opravilna);

            const res = await apiFetch(`/api/novprimer?${params.toString()}`, { method: "GET" });

            if (res.status === 404) {
                setSporocilo("Za to zadevo še ni shranjenega novega primera.");
                setStroskovnik(null);
                setStroskovnikValueKey(`not-found-${Date.now()}`);
                return;
            }

            if (!res.ok) {
                let msg = "";

                try {
                    const json = await res.json();
                    msg = json?.message || json?.title || "";
                } catch {
                    msg = await res.text().catch(() => "");
                }

                throw new Error(msg || "Napaka pri nalaganju novega primera.");
            }

            const data = await res.json();

            setPrimerId(data.id || null);
            setStevilkaSpisaIzvrsitelja(data.stevilkaSpisaIzvrsitelja || "");
            setOpravilnaStevilkaSodisca(data.opravilnaStevilkaSodisca || "");

            const u = data.upnik || {};
            setUpnikDavcna(u.davcnaAliMaticna || "");
            setAjpesUpnik(u.ajpesUpnik || null);
            setZastopanPo(u.zastopanPo || "");
            setPooblascenec(u.pooblascenec || "");
            setGlavnicaTerjatve(formatMoneySl(u.glavnicaTerjatve));
            setImeIzvrsitelja((old) => u.izvrsitelj || old);
            setTrrUpnika(formatirajTrrVnos(u.trr || ""));
            setTelefonUpnika(formatirajTelefonVnos(u.telefon || ""));
            setKontaktnaOseba(u.kontaktnaOseba || "");
            setGsmUpnika(formatirajTelefonVnos(u.gsm || ""));
            setEPostaUpnika(u.ePosta || "");
            setStatusUpnika(u.statusUpnika || "koncni");
            setDavcniZavezanec(Boolean(u.davcniZavezanec));
            setOpombeUpnika(u.opombe || "");
            setNazivNaRacun(u.nazivNaRacun || "");

            if (u.nazivBanke) {
                if (MOZNOSTI_BANK.includes(u.nazivBanke)) {
                    setNazivBanke(u.nazivBanke);
                    setPoljubnaBanka("");
                } else {
                    setNazivBanke("Druga banka");
                    setPoljubnaBanka(u.nazivBanke);
                }
            } else {
                setNazivBanke("");
                setPoljubnaBanka("");
            }

            const d = data.dolznik || {};
            setDolznikDavcna(d.davcnaAliMaticna || "");
            setAjpesDolznik(d.ajpesDolznik || null);
            setFizicnaOseba(d.fizicnaOseba || "");
            setTelefonDolznika(formatirajTelefonVnos(d.telefonDolznika || ""));
            setGsmDolznika(formatirajTelefonVnos(d.gsmDolznika || ""));
            setEPostaDolznika(d.ePostaDolznika || "");
            setDolznikoviTrrji(Array.isArray(d.dolznikoviTrrji) && d.dolznikoviTrrji.length ? d.dolznikoviTrrji : [{ trr: "" }]);
            setDolznikoveBanke(Array.isArray(d.dolznikoveBanke) && d.dolznikoveBanke.length ? d.dolznikoveBanke : [{ banka: "", poljubnaBanka: "" }]);
            setZnesekDolga(formatMoneySl(d.znesekDolga));
            setGlavnica(formatMoneySl(d.glavnica));
            setStroski(formatMoneySl(d.stroski));
            setSkupajDolgNaDan(formatMoneySl(d.skupajDolgNaDan));
            setObrestiOd(d.obrestiOd || "");
            setObrestiDo(d.obrestiDo || "");
            setPrejsnjiRubezi(
                Array.isArray(d.prejsnjiRubezi) && d.prejsnjiRubezi.length
                    ? d.prejsnjiRubezi.map((r) => ({
                        stevilkaRubeza: r.stevilkaRubeza || "",
                        izvrsitelj: r.izvrsitelj || imeIzvrsitelja || "",
                        stevilkaZadeve: r.stevilkaZadeve || "",
                        glavnicaZarubljena: formatMoneySl(r.glavnicaZarubljena),
                        predmetRubeza: r.predmetRubeza || "",
                        sredstvoRubeza: r.sredstvoRubeza || "",
                    }))
                    : [ustvariPrazniPrejsnjiRubez()]
            );

            const por = data.porocilo || {};
            setPorociloSodisce(por.sodisce || "");
            setPorociloZapSt(por.zapSt || "");
            setPorociloStAkta(por.stAkta || "");
            setPorociloDatumAkta(por.datumAkta || "");
            setPorociloVrstaIzvrsilnegaDejanja(por.vrstaIzvrsilnegaDejanja || "");
            setPorociloSredstvoInPredmetIzvrsbe(por.sredstvoInPredmetIzvrsbe || "");
            setPorociloOpravljenaDejanja(por.opravljenaDejanja || "");
            setPorociloDatumPravnomocnosti(por.datumPravnomocnosti || "");
            setPorociloVarscinaStatus(por.varscinaStatus || "");
            setPorociloVarscinaIzvrsitelj(formatMoneySl(por.varscinaIzvrsitelj));
            setPorociloVarscinaSodisce(formatMoneySl(por.varscinaSodisce));
            setPorociloDatumSklepa(por.datumSklepa || "");
            setPorociloVisinaIzterjanega(formatMoneySl(por.visinaIzterjanega));
            setPorociloNacinIzterjave(por.nacinIzterjave || "");
            setPorociloDatumOddajePorocila(por.datumOddajePorocila || "");
            setPorociloVisinaPlacila(formatMoneySl(por.visinaPlacila));
            setPorociloOpombePorocila(por.opombePorocila || "");

            setStroskovnik(null);
            setStroskovnikValueKey(data.id || `${spis}-${opravilna}-${Date.now()}`);
            setSporocilo("Nov primer je naložen iz baze.");
        } catch (e) {
            setNapaka(e?.message || "Napaka pri nalaganju novega primera.");
        } finally {
            setNalaganje(false);
        }
    };

    const zgradiPayload = () => {
        const koncniNazivBanke =
            nazivBanke === "Druga banka" && poljubnaBanka.trim()
                ? poljubnaBanka.trim()
                : nazivBanke;

        return {
            id: primerId,
            stevilkaSpisaIzvrsitelja: stevilkaSpisaIzvrsitelja.trim(),
            opravilnaStevilkaSodisca: opravilnaStevilkaSodisca.trim(),

            upnik: {
                davcnaAliMaticna: onlyDigits(upnikDavcna),
                zastopanPo: zastopanPo.trim(),
                pooblascenec: pooblascenec.trim(),
                glavnicaTerjatve: parseMoney(glavnicaTerjatve),
                izvrsitelj: imeIzvrsitelja,
                nazivBanke: koncniNazivBanke,
                trr: trrUpnika.replace(/\s/g, ""),
                telefon: telefonUpnika.replace(/\s/g, ""),
                kontaktnaOseba: kontaktnaOseba.trim(),
                gsm: gsmUpnika.replace(/\s/g, ""),
                ePosta: ePostaUpnika.trim() || null,
                statusUpnika,
                davcniZavezanec,
                opombe: opombeUpnika.trim(),
                nazivNaRacun: nazivNaRacun.trim(),
                ajpesUpnik,
            },

            dolznik: {
                davcnaAliMaticna: onlyDigits(dolznikDavcna),
                fizicnaOseba,
                dolznikoviTrrji,
                telefonDolznika: telefonDolznika.replace(/\s/g, ""),
                gsmDolznika: gsmDolznika.replace(/\s/g, ""),
                ePostaDolznika: ePostaDolznika.trim() || null,
                dolznikoveBanke,
                znesekDolga: parseMoney(znesekDolga),
                glavnica: parseMoney(glavnica),
                obrestiOd,
                obrestiDo,
                stroski: parseMoney(stroski),
                skupajDolgNaDan: parseMoney(skupajDolgNaDan),
                prejsnjiRubezi: prejsnjiRubezi.map((r) => ({
                    ...r,
                    glavnicaZarubljena: parseMoney(r.glavnicaZarubljena),
                })),
                ajpesDolznik,
            },

            porocilo: {
                sodisce: porociloSodisce.trim(),
                zapSt: porociloZapSt.trim(),
                stAkta: porociloStAkta.trim(),
                datumAkta: porociloDatumAkta,
                vrstaIzvrsilnegaDejanja: porociloVrstaIzvrsilnegaDejanja,
                sredstvoInPredmetIzvrsbe: porociloSredstvoInPredmetIzvrsbe,
                opravljenaDejanja: porociloOpravljenaDejanja.trim(),
                datumPravnomocnosti: porociloDatumPravnomocnosti,
                varscinaStatus: porociloVarscinaStatus,
                varscinaIzvrsitelj: parseMoney(porociloVarscinaIzvrsitelj),
                varscinaSodisce: parseMoney(porociloVarscinaSodisce),
                datumSklepa: porociloDatumSklepa,
                visinaIzterjanega: parseMoney(porociloVisinaIzterjanega),
                nacinIzterjave: porociloNacinIzterjave,
                datumOddajePorocila: porociloDatumOddajePorocila,
                visinaPlacila: parseMoney(porociloVisinaPlacila),
                opombePorocila: porociloOpombePorocila.trim(),
            },
        };
    };

    const validirajPredShranjevanjem = () => {
        const spis = stevilkaSpisaIzvrsitelja.trim();
        const opravilna = opravilnaStevilkaSodisca.trim();
        const upnikStevke = onlyDigits(upnikDavcna);
        const dolznikStevke = onlyDigits(dolznikDavcna);

        if (!spis && !opravilna) return "Vnesite št. spisa izvršitelja ali opr. št. sodišča.";
        if (!(upnikStevke.length === 8 || upnikStevke.length === 10)) return "Pri upniku vpiši davčno (8) ali matično (10) številk.";
        if (!(dolznikStevke.length === 8 || dolznikStevke.length === 10)) return "Pri dolžniku vpiši davčno (8) ali matično (10) številk.";
        if (!jeVeljavenEmail(ePostaUpnika)) return "E-pošta upnika ni veljavna.";
        if (!jeVeljavenEmail(ePostaDolznika)) return "E-pošta dolžnika ni veljavna.";

        return "";
    };

    const shraniPrimer = async () => {
        if (shranjevanje) return;

        setNapaka("");
        setSporocilo("");

        const validacija = validirajPredShranjevanjem();

        if (validacija) {
            setNapaka(validacija);
            return;
        }

        const payload = zgradiPayload();

        setShranjevanje(true);

        try {
            const res = await apiFetch("/api/novprimer", {
                method: "POST",
                body: payload,
            });

            if (!res.ok) {
                let msg = "";

                try {
                    const json = await res.json();
                    const errors = json?.errors || {};
                    const keys = Object.keys(errors);

                    if (keys.length > 0) msg = errors[keys[0]]?.[0] || "";

                    msg = msg || json?.message || json?.title || "";
                } catch {
                    msg = await res.text().catch(() => "");
                }

                throw new Error(msg || "Napaka pri shranjevanju novega primera.");
            }

            const saved = await res.json().catch(() => ({}));

            setPrimerId(saved?.id || primerId);
            setUpnikDavcna(onlyDigits(upnikDavcna));
            setDolznikDavcna(onlyDigits(dolznikDavcna));
            setStroskovnikValueKey(saved?.id || stroskovnikValueKey);
            setSporocilo("Uspešno shranjeno.");
        } catch (e) {
            setNapaka(e?.message || "Napaka pri shranjevanju novega primera.");
        } finally {
            setShranjevanje(false);
        }
    };

    const obNatisni = async () => {
        setNapaka("");

        const oldFrame = document.getElementById("nov-primer-print-frame");
        if (oldFrame) oldFrame.remove();

        const iframe = document.createElement("iframe");
        iframe.id = "nov-primer-print-frame";
        iframe.title = "Tiskanje novega primera";
        iframe.setAttribute("aria-hidden", "true");

        iframe.style.position = "fixed";
        iframe.style.left = "-10000px";
        iframe.style.top = "0";
        iframe.style.width = "1px";
        iframe.style.height = "1px";
        iframe.style.border = "0";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";

        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDocument = frameWindow?.document;

        if (!frameWindow || !frameDocument) {
            iframe.remove();
            setNapaka("Tiskanje ni mogoče zagnati.");
            return;
        }

        frameDocument.open();
        frameDocument.write(buildNovPrimerExportHtml());
        frameDocument.close();

        await waitForImagesIn(frameDocument);
        await new Promise((resolve) => setTimeout(resolve, 250));

        const cleanup = () => {
            frameWindow.removeEventListener("afterprint", cleanup);

            setTimeout(() => {
                const currentFrame = document.getElementById("nov-primer-print-frame");
                if (currentFrame) currentFrame.remove();
            }, 300);
        };

        frameWindow.addEventListener("afterprint", cleanup);

        try {
            frameWindow.focus();
            frameWindow.print();
        } catch {
            iframe.remove();
            setNapaka("Napaka pri tiskanju.");
        }
    };

    const obIzvoziPdf = async () => {
        if (izvoz) return;

        setNapaka("");
        setIzvoz(true);

        const host = document.createElement("div");
        host.style.position = "fixed";
        host.style.left = "-10000px";
        host.style.top = "0";
        host.style.width = `${NOV_PRIMER_EXPORT_WIDTH_PX}px`;
        host.style.background = "#ffffff";
        host.style.pointerEvents = "none";
        host.style.opacity = "1";

        host.innerHTML = `
            <style>${getNovPrimerExportCss()}</style>
            ${buildNovPrimerExportBody()}
        `;

        document.body.appendChild(host);

        try {
            if (document.fonts?.ready) {
                await document.fonts.ready.catch(() => { });
            }

            await waitForImagesIn(host);
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => requestAnimationFrame(resolve));

            const pdf = new jsPDF("p", "mm", "a4", true);

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 10;
            const usableWidth = pageWidth - margin * 2;
            const usableHeight = pageHeight - margin * 2;
            const blockGap = 2;

            let currentY = margin;
            let hasContent = false;

            const blocks = Array.from(host.querySelectorAll(".np-export-block")).filter(
                (el) => String(el.textContent || "").trim().length > 0
            );

            const addPageIfNeeded = (heightMm) => {
                if (!hasContent) return;

                if (currentY + heightMm > pageHeight - margin) {
                    pdf.addPage();
                    currentY = margin;
                    hasContent = false;
                }
            };

            const addCanvasToPdf = (canvas) => {
                if (!canvas || !canvas.width || !canvas.height) return;

                const pxPerMm = canvas.width / usableWidth;
                const fullHeightMm = canvas.height / pxPerMm;

                if (fullHeightMm <= usableHeight) {
                    addPageIfNeeded(fullHeightMm);

                    const imgData = canvas.toDataURL("image/jpeg", 0.92);

                    pdf.addImage(
                        imgData,
                        "JPEG",
                        margin,
                        currentY,
                        usableWidth,
                        fullHeightMm,
                        undefined,
                        "FAST"
                    );

                    currentY += fullHeightMm + blockGap;
                    hasContent = true;
                    return;
                }

                let sourceY = 0;
                const maxSliceHeightPx = Math.floor(usableHeight * pxPerMm);

                while (sourceY < canvas.height) {
                    if (hasContent) {
                        pdf.addPage();
                        currentY = margin;
                        hasContent = false;
                    }

                    const sliceHeightPx = Math.min(maxSliceHeightPx, canvas.height - sourceY);

                    const sliceCanvas = document.createElement("canvas");
                    sliceCanvas.width = canvas.width;
                    sliceCanvas.height = sliceHeightPx;

                    const ctx = sliceCanvas.getContext("2d");

                    ctx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        canvas.width,
                        sliceHeightPx,
                        0,
                        0,
                        canvas.width,
                        sliceHeightPx
                    );

                    const sliceHeightMm = sliceHeightPx / pxPerMm;
                    const imgData = sliceCanvas.toDataURL("image/jpeg", 0.9);

                    pdf.addImage(
                        imgData,
                        "JPEG",
                        margin,
                        currentY,
                        usableWidth,
                        sliceHeightMm,
                        undefined,
                        "FAST"
                    );

                    sourceY += sliceHeightPx;
                    currentY = margin + sliceHeightMm + blockGap;
                    hasContent = true;
                }
            };

            for (const block of blocks) {
                const canvas = await html2canvas(block, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                    removeContainer: true,
                    windowWidth: NOV_PRIMER_EXPORT_WIDTH_PX,
                });

                addCanvasToPdf(canvas);
            }

            const ime = filenameSafe(
                stevilkaSpisaIzvrsitelja ||
                opravilnaStevilkaSodisca ||
                primerId ||
                "nov-primer"
            );

            pdf.save(`${ime}.pdf`);
        } catch (e) {
            setNapaka(e?.message || "Napaka pri izvozu PDF.");
        } finally {
            document.body.removeChild(host);
            setIzvoz(false);
        }
    };

    const obIzvoziExcel = () => {
        try {
            const rows = [
                ["REPUBLIKA SLOVENIJA", "", "ID za DDV", prikazi(prijavljenUporabnik?.idZaDdv)],
                [prikazi(prijavljenUporabnik?.polnoIme || imeIzvrsitelja), "", "Matična številka", prikazi(prijavljenUporabnik?.maticnaStevilka)],
                [prikazi(prijavljenUporabnik?.naslov), "", "TRR", prikazi(prijavljenUporabnik?.transakcijskiRacunStevilka)],
                [prikazi(prijavljenUporabnik?.gsm), "", "Banka", prikazi(prijavljenUporabnik?.nazivBanke)],
                [prikazi(prijavljenUporabnik?.email), "", "", ""],
                [],
                ["Št. spisa izvršitelja", stevilkaSpisaIzvrsitelja, "Opr. št. sodišča", opravilnaStevilkaSodisca],
                [],
                ["UPNIK", ""],
                ["Davčna/matična", upnikDavcna],
                ["Naziv", imeUpnika],
                ["Naslov", naslovUpnika],
                ["Pošta", postaUpnika],
                ["Država", drzavaUpnika],
                ["Davčna št.", davcnaStevilkaUpnika],
                ["Pooblaščenec", pooblascenec],
                ["Zastopan po", zastopanPo],
                ["Glavnica terjatve", parseMoney(glavnicaTerjatve)],
                ["Banka", nazivBanke === "Druga banka" ? poljubnaBanka : nazivBanke],
                ["TRR", trrUpnika],
                ["GSM", gsmUpnika],
                ["Kontakt", telefonUpnika],
                ["Kontaktna oseba", kontaktnaOseba],
                ["E-pošta", ePostaUpnika],
                ["Status upnika", statusUpnika],
                ["Davčni zavezanec", davcniZavezanec ? "Da" : "Ne"],
                ["Opombe", opombeUpnika],
                ["Naziv na račun", nazivNaRacun],
                [],
                ["DOLŽNIK", ""],
                ["Davčna/matična", dolznikDavcna],
                ["Naziv", imeDolznika],
                ["Naslov", naslovDolznika],
                ["Pošta", postaDolznika],
                ["Država", drzavaDolznika],
                ["Davčna št.", davcnaStevilkaDolznika],
                ["Fizična oseba", fizicnaOseba],
                ["GSM", gsmDolznika],
                ["Kontakt", telefonDolznika],
                ["E-pošta", ePostaDolznika],
                ["Glavnica", parseMoney(glavnica)],
                ["Stroški", parseMoney(stroski)],
                ["Skupaj dolg na dan", parseMoney(skupajDolgNaDan)],
                ["Znesek dolga", parseMoney(znesekDolga)],
                ["Obresti od", obrestiOd],
                ["Obresti do", obrestiDo],
                [],
                ["PREDHODNI RUBEŽI", ""],
                ["Kateri rubež", "Predmet", "Sredstvo", "Glavnica zarubljena"],
                ...prejsnjiRubezi.map((r) => [
                    r.stevilkaRubeza,
                    r.predmetRubeza,
                    r.sredstvoRubeza,
                    parseMoney(r.glavnicaZarubljena),
                ]),
                [],
                ["STROŠKOVNIK", ""],
                ["Skupaj brez DDV", stroskovnik?.sum ?? ""],
                ["DDV", stroskovnik?.ddv ?? ""],
                ["Skupaj z DDV", stroskovnik?.total ?? ""],
                [],
                ["POROČILO / PODATKI ZADEVE", ""],
                ["Sodišče", porociloSodisce],
                ["Zap. št.", porociloZapSt],
                ["Št. akta", porociloStAkta],
                ["Datum akta", porociloDatumAkta],
                ["Vrsta izvršilnega dejanja", porociloVrstaIzvrsilnegaDejanja],
                ["Sredstvo in predmet izvršbe", porociloSredstvoInPredmetIzvrsbe],
                ["Opravljena dejanja", porociloOpravljenaDejanja],
                ["Datum pravnomočnosti", porociloDatumPravnomocnosti],
                ["Varščina – status", porociloVarscinaStatus],
                ["Varščina izvršitelj", parseMoney(porociloVarscinaIzvrsitelj)],
                ["Varščina sodišče", parseMoney(porociloVarscinaSodisce)],
                ["Datum sklepa", porociloDatumSklepa],
                ["Višina izterjanega", parseMoney(porociloVisinaIzterjanega)],
                ["Način izterjave", porociloNacinIzterjave],
                ["Datum oddaje poročila", porociloDatumOddajePorocila],
                ["Višina plačila", parseMoney(porociloVisinaPlacila)],
                ["Opombe poročila", porociloOpombePorocila],
            ];

            const ws = XLSX.utils.aoa_to_sheet(rows);

            ws["!cols"] = [
                { wch: 34 },
                { wch: 44 },
                { wch: 28 },
                { wch: 28 },
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Nov primer");

            const ime = filenameSafe(
                stevilkaSpisaIzvrsitelja ||
                opravilnaStevilkaSodisca ||
                primerId ||
                "nov-primer"
            );

            XLSX.writeFile(wb, `${ime}.xlsx`);
        } catch {
            setNapaka("Napaka pri izvozu Excel.");
        }
    };

    return (
        <main className="np-page">
            <section
                ref={printRef}
                className={`np-card np-printArea ${izvoz ? "np-exportMode" : ""}`}
            >
                <div className="pod-header np-headerWithUser">
                    <div className="np-headerTopLine">
                        <div className="np-headerTitleFull">
                            <h1 className="pod-header-title">Nov primer</h1>
                            <p className="pod-header-subtitle">
                                Skupni vnos podatkov o upniku, dolžniku, stroškovniku in poročilu.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="btn-nazaj np-noPrint"
                            onClick={() => navigate("/profil", { replace: true })}
                        >
                            <span className="btn-nazaj__icon" aria-hidden="true">←</span>
                        </button>
                    </div>

                    <div className="np-headerUserLine">
                        {prijavljenUporabnik && (
                            <aside
                                className="np-currentUser np-noPrint"
                                aria-label="Podatki prijavljenega uporabnika"
                            >
                                <div className="np-currentUser__tower np-currentUser__tower--left">
                                    <div className="np-currentUser__logoBox">
                                        <img
                                            src={NOV_PRIMER_LOGO_SRC}
                                            alt="IZVRŠITELJ"
                                            className="np-currentUser__logo"
                                            loading="eager"
                                        />

                                        <div className="np-currentUser__logoText">
                                            REPUBLIKA SLOVENIJA
                                        </div>
                                    </div>

                                    <div className="np-currentUser__mainData">
                                        <strong>{prikazi(prijavljenUporabnik.polnoIme)}</strong>
                                        <span>{prikazi(prijavljenUporabnik.naslov)}</span>
                                        <span>{prikazi(prijavljenUporabnik.gsm)}</span>
                                        <span>{prikazi(prijavljenUporabnik.email)}</span>
                                    </div>
                                </div>

                                <div className="np-currentUser__tower np-currentUser__tower--right">
                                    <div className="np-currentUser__row">
                                        <span>ID za DDV</span>
                                        <strong>{prikazi(prijavljenUporabnik.idZaDdv)}</strong>
                                    </div>

                                    <div className="np-currentUser__row">
                                        <span>Matična številka</span>
                                        <strong>{prikazi(prijavljenUporabnik.maticnaStevilka)}</strong>
                                    </div>

                                    <div className="np-currentUser__row">
                                        <span>TRR</span>
                                        <strong>{prikazi(prijavljenUporabnik.transakcijskiRacunStevilka)}</strong>
                                    </div>

                                    <div className="np-currentUser__row">
                                        <span>Banka</span>
                                        <strong>{prikazi(prijavljenUporabnik.nazivBanke)}</strong>
                                    </div>
                                </div>
                            </aside>
                        )}
                    </div>
                </div>

                <section className="np-top">
                    <label className="pod-field">
                        <span className="pod-label">Št. spisa izvršitelja</span>
                        <input
                            className="pod-input"
                            value={stevilkaSpisaIzvrsitelja}
                            onChange={(e) => setStevilkaSpisaIzvrsitelja(e.target.value)}
                        />
                    </label>

                    <label className="pod-field">
                        <span className="pod-label">Opr. št. sodišča</span>
                        <input
                            className="pod-input"
                            value={opravilnaStevilkaSodisca}
                            onChange={(e) => setOpravilnaStevilkaSodisca(e.target.value)}
                        />
                    </label>

                    <button
                        type="button"
                        className="pod-btn pod-btn--ghost np-noPrint"
                        onClick={naloziPrimerPoZadevi}
                        disabled={nalaganje}
                    >
                        {nalaganje ? "Nalagam…" : "Naloži primer"}
                    </button>
                </section>

                {napaka && <div className="pod-alert pod-alert--error">{napaka}</div>}
                {sporocilo && <div className="np-message">{sporocilo}</div>}

                <section className="np-grid">
                    <div className="np-column">
                        <h2 className="pod-sectionTitle">Podatki o upniku</h2>

                        <div className="pod-inlineForm pod-inlineForm--ajpes">
                            <label className="pod-field pod-field--grow">
                                <span className="pod-label">Matična ali davčna številka (24995975)</span>
                                <input
                                    className="pod-input"
                                    value={upnikDavcna}
                                    inputMode="numeric"
                                    onChange={(e) => setUpnikDavcna(onlyDigits(e.target.value))}
                                />
                            </label>

                            <button
                                type="button"
                                className="pod-btn np-noPrint"
                                onClick={() => pridobiAjpes("upnik")}
                                disabled={nalaganje}
                            >
                                Pridobi upnika
                            </button>
                        </div>

                        <label className="pod-field">
                            <span className="pod-label">Naziv upnika</span>
                            <input className="pod-input pod-input--readonly" value={imeUpnika} readOnly />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Naslov</span>
                            <input className="pod-input pod-input--readonly" value={naslovUpnika} readOnly />
                        </label>

                        <div className="pod-inlineInputs">
                            <input className="pod-input pod-input--readonly" value={postaUpnika} readOnly placeholder="Pošta" />
                            <span className="pod-inlineSeparator">/</span>
                            <input className="pod-input pod-input--readonly" value={drzavaUpnika} readOnly placeholder="Država" />
                        </div>

                        <label className="pod-field">
                            <span className="pod-label">Davčna št.</span>
                            <input className="pod-input pod-input--readonly" value={davcnaStevilkaUpnika} readOnly />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">E-pošta</span>
                            <input
                                className="pod-input"
                                type="email"
                                value={ePostaUpnika}
                                onChange={(e) => setEPostaUpnika(e.target.value)}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Pooblaščenec</span>
                            <input
                                className="pod-input"
                                value={pooblascenec}
                                onChange={(e) => setPooblascenec(e.target.value)}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Zastopan po</span>
                            <input
                                className="pod-input"
                                value={zastopanPo}
                                onChange={(e) => setZastopanPo(e.target.value)}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Glavnica terjatve</span>
                            <div className="pod-moneyWrap">
                                <input
                                    className="pod-input pod-moneyInput"
                                    value={glavnicaTerjatve}
                                    inputMode="decimal"
                                    onChange={(e) => setGlavnicaTerjatve(cleanMoneyInput(e.target.value))}
                                    onBlur={(e) => setGlavnicaTerjatve(formatMoneySl(e.target.value))}
                                />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Banka</span>
                            <select
                                className="pod-input"
                                value={nazivBanke}
                                onChange={(e) => {
                                    setNazivBanke(e.target.value);
                                    if (e.target.value !== "Druga banka") setPoljubnaBanka("");
                                }}
                            >
                                <option value="">- izberi banko</option>
                                {MOZNOSTI_BANK.map((b) => (
                                    <option key={b} value={b}>{b}</option>
                                ))}
                            </select>
                        </label>

                        {nazivBanke === "Druga banka" && (
                            <label className="pod-field">
                                <span className="pod-label">Naziv druge banke</span>
                                <input
                                    className="pod-input"
                                    value={poljubnaBanka}
                                    onChange={(e) => setPoljubnaBanka(e.target.value)}
                                />
                            </label>
                        )}

                        <label className="pod-field">
                            <span className="pod-label">TRR</span>
                            <div className="pod-ibanWrapper">
                                <span className="pod-ibanPrefix">SI</span>
                                <input
                                    className="pod-input pod-ibanInput"
                                    value={trrUpnika}
                                    inputMode="numeric"
                                    onChange={(e) => setTrrUpnika(formatirajTrrVnos(e.target.value))}
                                />
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">GSM</span>
                            <input
                                className="pod-input"
                                value={gsmUpnika}
                                onChange={(e) => setGsmUpnika(formatirajTelefonVnos(e.target.value))}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Kontakt</span>
                            <input
                                className="pod-input"
                                value={telefonUpnika}
                                onChange={(e) => setTelefonUpnika(formatirajTelefonVnos(e.target.value))}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Kontaktna oseba</span>
                            <input
                                className="pod-input"
                                value={kontaktnaOseba}
                                onChange={(e) => setKontaktnaOseba(e.target.value)}
                            />
                        </label>

                        <div className="np-options">
                            <strong>Status upnika</strong>

                            {[
                                ["koncni", "Končni potrošnik"],
                                ["eu_id", "Upnik iz EU z ID-številko"],
                                ["zavezanec_id", "Zavezanec za DDV z ID-številko"],
                                ["drugo", "Drugo"],
                            ].map(([value, label]) => (
                                <label key={value}>
                                    <input
                                        type="radio"
                                        name="statusUpnika"
                                        value={value}
                                        checked={statusUpnika === value}
                                        onChange={(e) => setStatusUpnika(e.target.value)}
                                    />
                                    {label}
                                </label>
                            ))}

                            <strong>Davčni zavezanec</strong>

                            <label>
                                <input
                                    type="radio"
                                    name="davcniZavezanec"
                                    checked={davcniZavezanec === true}
                                    onChange={() => setDavcniZavezanec(true)}
                                />
                                Da
                            </label>

                            <label>
                                <input
                                    type="radio"
                                    name="davcniZavezanec"
                                    checked={davcniZavezanec === false}
                                    onChange={() => setDavcniZavezanec(false)}
                                />
                                Ne
                            </label>
                        </div>

                        <label className="pod-field">
                            <span className="pod-label">Opombe</span>
                            <textarea
                                className="pod-input pod-textarea"
                                rows={3}
                                value={opombeUpnika}
                                onChange={(e) => setOpombeUpnika(e.target.value)}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Naziv, ki se napiše na račun, avans, dobropis</span>
                            <textarea
                                className="pod-input pod-textarea"
                                rows={3}
                                value={nazivNaRacun}
                                onChange={(e) => setNazivNaRacun(e.target.value)}
                            />
                        </label>
                    </div>

                    <div className="np-column">
                        <h2 className="pod-sectionTitle">Podatki o dolžniku</h2>

                        <div className="pod-inlineForm pod-inlineForm--ajpes">
                            <label className="pod-field pod-field--grow">
                                <span className="pod-label">Matična ali davčna številka (24995975)</span>
                                <input
                                    className="pod-input"
                                    value={dolznikDavcna}
                                    inputMode="numeric"
                                    onChange={(e) => setDolznikDavcna(onlyDigits(e.target.value))}
                                />
                            </label>

                            <button
                                type="button"
                                className="pod-btn np-noPrint"
                                onClick={() => pridobiAjpes("dolznik")}
                                disabled={nalaganje}
                            >
                                Pridobi dolžnika
                            </button>
                        </div>

                        <label className="pod-field">
                            <span className="pod-label">Ime / firma</span>
                            <input className="pod-input pod-input--readonly" value={imeDolznika} readOnly />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Naslov</span>
                            <input className="pod-input pod-input--readonly" value={naslovDolznika} readOnly />
                        </label>

                        <div className="pod-inlineInputs">
                            <input className="pod-input pod-input--readonly" value={postaDolznika} readOnly placeholder="Pošta" />
                            <span className="pod-inlineSeparator">/</span>
                            <input className="pod-input pod-input--readonly" value={drzavaDolznika} readOnly placeholder="Država" />
                        </div>

                        <label className="pod-field">
                            <span className="pod-label">Davčna št.</span>
                            <input className="pod-input pod-input--readonly" value={davcnaStevilkaDolznika} readOnly />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Fizična oseba</span>
                            <input
                                className="pod-input"
                                value={fizicnaOseba}
                                onChange={(e) => setFizicnaOseba(e.target.value)}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">GSM</span>
                            <input
                                className="pod-input"
                                value={gsmDolznika}
                                onChange={(e) => setGsmDolznika(formatirajTelefonVnos(e.target.value))}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Kontakt</span>
                            <input
                                className="pod-input"
                                value={telefonDolznika}
                                onChange={(e) => setTelefonDolznika(formatirajTelefonVnos(e.target.value))}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">E-pošta</span>
                            <input
                                className="pod-input"
                                type="email"
                                value={ePostaDolznika}
                                onChange={(e) => setEPostaDolznika(e.target.value)}
                            />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Glavnica</span>
                            <div className="pod-moneyWrap">
                                <input
                                    className="pod-input pod-moneyInput"
                                    value={glavnica}
                                    inputMode="decimal"
                                    onChange={(e) => setGlavnica(cleanMoneyInput(e.target.value))}
                                    onBlur={(e) => setGlavnica(formatMoneySl(e.target.value))}
                                />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Stroški</span>
                            <div className="pod-moneyWrap">
                                <input
                                    className="pod-input pod-moneyInput"
                                    value={stroski}
                                    inputMode="decimal"
                                    onChange={(e) => setStroski(cleanMoneyInput(e.target.value))}
                                    onBlur={(e) => setStroski(formatMoneySl(e.target.value))}
                                />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Skupaj dolg na dan</span>
                            <div className="pod-moneyWrap">
                                <input
                                    className="pod-input pod-moneyInput"
                                    value={skupajDolgNaDan}
                                    inputMode="decimal"
                                    onChange={(e) => setSkupajDolgNaDan(cleanMoneyInput(e.target.value))}
                                    onBlur={(e) => setSkupajDolgNaDan(formatMoneySl(e.target.value))}
                                />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Znesek dolga</span>
                            <div className="pod-moneyWrap">
                                <input
                                    className="pod-input pod-moneyInput"
                                    value={znesekDolga}
                                    inputMode="decimal"
                                    onChange={(e) => setZnesekDolga(cleanMoneyInput(e.target.value))}
                                    onBlur={(e) => setZnesekDolga(formatMoneySl(e.target.value))}
                                />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <div className="pod-field pod-field--inline">
                            <span className="pod-label">Obresti od – do</span>
                            <div className="pod-inlineInputs">
                                <input className="pod-input" type="date" value={obrestiOd} onChange={(e) => setObrestiOd(e.target.value)} />
                                <span className="pod-inlineSeparator">–</span>
                                <input className="pod-input" type="date" value={obrestiDo} onChange={(e) => setObrestiDo(e.target.value)} />
                            </div>
                        </div>

                        <hr className="pod-separator" />

                        <h3 className="pod-sectionTitle">Predhodno zarubljene premičnine</h3>

                        {prejsnjiRubezi.map((r, index) => (
                            <div key={index} className="pod-prevCard">
                                <div className="pod-prevGrid">
                                    <label className="pod-field">
                                        <span className="pod-label">Kateri rubež</span>
                                        <select
                                            className="pod-input"
                                            value={r.stevilkaRubeza}
                                            onChange={(e) => {
                                                const next = [...prejsnjiRubezi];
                                                next[index] = { ...next[index], stevilkaRubeza: e.target.value };
                                                setPrejsnjiRubezi(next);
                                            }}
                                        >
                                            <option value="">- izberi</option>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <option key={n} value={String(n)}>{n}. rubež</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="pod-field">
                                        <span className="pod-label">Predmet rubeža</span>
                                        <select
                                            className="pod-input"
                                            value={r.predmetRubeza}
                                            onChange={(e) => {
                                                const next = [...prejsnjiRubezi];
                                                next[index] = { ...next[index], predmetRubeza: e.target.value };
                                                setPrejsnjiRubezi(next);
                                            }}
                                        >
                                            <option value="">- izberi</option>
                                            {MOZNOSTI_PREDMETA.map((x) => (
                                                <option key={x} value={x}>{x}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="pod-field">
                                        <span className="pod-label">Sredstvo rubeža</span>
                                        <select
                                            className="pod-input"
                                            value={r.sredstvoRubeza}
                                            onChange={(e) => {
                                                const next = [...prejsnjiRubezi];
                                                next[index] = { ...next[index], sredstvoRubeza: e.target.value };
                                                setPrejsnjiRubezi(next);
                                            }}
                                        >
                                            <option value="">- izberi</option>
                                            {MOZNOSTI_SREDSTVA.map((x) => (
                                                <option key={x} value={x}>{x}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                {prejsnjiRubezi.length > 1 && (
                                    <button
                                        type="button"
                                        className="pod-btn pod-btn--ghost np-noPrint"
                                        onClick={() => setPrejsnjiRubezi((old) => old.filter((_, i) => i !== index))}
                                    >
                                        Odstrani vrstico
                                    </button>
                                )}
                            </div>
                        ))}

                        <button
                            type="button"
                            className="pod-btn pod-btn--ghost np-noPrint"
                            onClick={() =>
                                setPrejsnjiRubezi((old) => [
                                    ...old,
                                    {
                                        ...ustvariPrazniPrejsnjiRubez(),
                                        izvrsitelj: imeIzvrsitelja || "",
                                        stevilkaZadeve: opravilnaStevilkaSodisca || stevilkaSpisaIzvrsitelja || "",
                                    },
                                ])
                            }
                        >
                            + Dodaj predhodni rubež
                        </button>
                    </div>
                </section>

                <section className="np-stroskovnik">
                    <Stroskovnik
                        key={stroskovnikValueKey}
                        embedded
                        showBackButton={false}
                        showActions={false}
                        showPartyLookup={false}
                        showCaseHeader={false}
                        showPartyData={false}
                        showPrintCaseData={false}
                        initialPrincipal={osnovnaGlavnicaZaObresti}
                        initialCase={stroskovnikInitialCase}
                        initialValue={stroskovnik}
                        valueKey={stroskovnikValueKey}
                        onChange={setStroskovnik}
                    />
                </section>

                <section className="np-report">
                    <div className="np-reportHeader">
                        <h2 className="pod-sectionTitle">Poročilo / podatki zadeve</h2>
                    </div>

                    <div className="np-reportGrid">
                        <label className="pod-field">
                            <span className="pod-label">Sodišče</span>
                            <input className="pod-input" value={porociloSodisce} onChange={(e) => setPorociloSodisce(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Zap. št.</span>
                            <input className="pod-input" value={porociloZapSt} onChange={(e) => setPorociloZapSt(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Št. akta</span>
                            <input className="pod-input" value={porociloStAkta} onChange={(e) => setPorociloStAkta(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Datum akta</span>
                            <input className="pod-input" type="date" value={porociloDatumAkta} onChange={(e) => setPorociloDatumAkta(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Vrsta izvršilnega dejanja</span>
                            <select className="pod-input" value={porociloVrstaIzvrsilnegaDejanja} onChange={(e) => setPorociloVrstaIzvrsilnegaDejanja(e.target.value)}>
                                <option value="">- izberi</option>
                                {VRSTE_IZVRSILNEGA_DEJANJA.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Sredstvo in predmet izvršbe</span>
                            <select className="pod-input" value={porociloSredstvoInPredmetIzvrsbe} onChange={(e) => setPorociloSredstvoInPredmetIzvrsbe(e.target.value)}>
                                <option value="">- izberi</option>
                                {SREDSTVA_IN_PREDMETI_IZVRSBE.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                        </label>

                        <label className="pod-field np-reportWide">
                            <span className="pod-label">Opravljena dejanja</span>
                            <textarea className="pod-input pod-textarea" rows={3} value={porociloOpravljenaDejanja} onChange={(e) => setPorociloOpravljenaDejanja(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Datum pravnomočnosti</span>
                            <input className="pod-input" type="date" value={porociloDatumPravnomocnosti} onChange={(e) => setPorociloDatumPravnomocnosti(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Varščina – status</span>
                            <select className="pod-input" value={porociloVarscinaStatus} onChange={(e) => setPorociloVarscinaStatus(e.target.value)}>
                                <option value="">- izberi</option>
                                {VARSCINA_STATUSI.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Varščina (izvršitelj)</span>
                            <div className="pod-moneyWrap">
                                <input className="pod-input pod-moneyInput" inputMode="decimal" value={porociloVarscinaIzvrsitelj} onChange={(e) => setPorociloVarscinaIzvrsitelj(cleanMoneyInput(e.target.value))} onBlur={(e) => setPorociloVarscinaIzvrsitelj(formatMoneySl(e.target.value))} />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Varščina (sodišče)</span>
                            <div className="pod-moneyWrap">
                                <input className="pod-input pod-moneyInput" inputMode="decimal" value={porociloVarscinaSodisce} onChange={(e) => setPorociloVarscinaSodisce(cleanMoneyInput(e.target.value))} onBlur={(e) => setPorociloVarscinaSodisce(formatMoneySl(e.target.value))} />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Datum sklepa</span>
                            <input className="pod-input" type="date" value={porociloDatumSklepa} onChange={(e) => setPorociloDatumSklepa(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Višina izterjanega</span>
                            <div className="pod-moneyWrap">
                                <input className="pod-input pod-moneyInput" inputMode="decimal" value={porociloVisinaIzterjanega} onChange={(e) => setPorociloVisinaIzterjanega(cleanMoneyInput(e.target.value))} onBlur={(e) => setPorociloVisinaIzterjanega(formatMoneySl(e.target.value))} />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Način izterjave</span>
                            <select className="pod-input" value={porociloNacinIzterjave} onChange={(e) => setPorociloNacinIzterjave(e.target.value)}>
                                <option value="">- izberi</option>
                                {NACINI_IZTERJAVE.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Datum oddaje poročila</span>
                            <input className="pod-input" type="date" value={porociloDatumOddajePorocila} onChange={(e) => setPorociloDatumOddajePorocila(e.target.value)} />
                        </label>

                        <label className="pod-field">
                            <span className="pod-label">Višina plačila</span>
                            <div className="pod-moneyWrap">
                                <input className="pod-input pod-moneyInput" inputMode="decimal" value={porociloVisinaPlacila} onChange={(e) => setPorociloVisinaPlacila(cleanMoneyInput(e.target.value))} onBlur={(e) => setPorociloVisinaPlacila(formatMoneySl(e.target.value))} />
                                <span className="pod-moneySuffix">€</span>
                            </div>
                        </label>

                        <label className="pod-field np-reportWide">
                            <span className="pod-label">Opombe poročila</span>
                            <textarea className="pod-input pod-textarea" rows={3} value={porociloOpombePorocila} onChange={(e) => setPorociloOpombePorocila(e.target.value)} />
                        </label>
                    </div>
                </section>

                <div className="np-actions np-noPrint">
                    <button type="button" className="pod-btn pod-btn--ghost" onClick={obIzvoziPdf} disabled={izvoz}>
                        {izvoz ? "Izvažam PDF…" : "Izvozi PDF"}
                    </button>

                    <button type="button" className="pod-btn pod-btn--ghost" onClick={obIzvoziExcel}>
                        Izvozi Excel
                    </button>

                    <button type="button" className="pod-btn pod-btn--ghost" onClick={obNatisni}>
                        Natisni
                    </button>

                    <button type="button" className="pod-btn" onClick={shraniPrimer} disabled={shranjevanje}>
                        {shranjevanje ? "Shranjujem…" : primerId ? "Posodobi nov primer" : "Shrani nov primer"}
                    </button>
                </div>
            </section>
        </main>
    );
}